import { graphHooks } from '@tunasong/graph-lib/react'
import { dayjs } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import type { Comment, Persisted, Task } from '@tunasong/schemas'
import { isAgentAuthoredComment } from '@tunasong/schemas'
import { Checkbox } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'
import { useChatActions } from './chat-actions.hook.js'
import { useChatToast } from './chat-toast.js'
import { taskFailedToast } from './task-failed.js'
import styles from './task-row.module.css'
import { taskText } from './task-text.js'

export interface TaskRowProps {
  comment: Persisted<Comment>
  /** The comment's body */
  children: ReactNode
}

/** T7: "Done by {name} · {time ago}", in the row's own name and relative-time helpers */
const DoneLine: FC<{ task: Task }> = ({ task }) => {
  const { profile } = graphHooks.useProfile(task.doneBy)
  const when = task.doneAt ? ` · ${dayjs(task.doneAt).fromNow()}` : ''
  return (
    <div className={styles.done}>
      Done by {profile?.name ?? 'Unknown'}
      {when}
    </div>
  )
}

/**
 * A comment's body, and when it is a task (chat tasks M2, GH #3041): a checkbox before the text, named by it; done, the
 * text struck through, still in the primary text colour, with T7 under it. Anyone who can comment can tick or untick;
 * it acts at once with no toast, as the checkbox is its own feedback. If the server refuses, it goes back and the chat's
 * toast says so, with Try again. An agent's comment is never a task here.
 */
export const TaskRow: FC<TaskRowProps> = props => {
  const { comment, children } = props
  const { tick, untick } = useChatActions()
  const { userId } = useCurrentUser()
  const showToast = useChatToast()
  /** What was just ticked or unticked, shown until the server's version of the comment arrives */
  const [pending, setPending] = useState<{ from: Task | undefined; to: Task } | null>(null)

  if (!comment.task || isAgentAuthoredComment(comment)) {
    return <>{children}</>
  }
  const task = pending && pending.from === comment.task ? pending.to : comment.task
  const isDone = Boolean(task.doneBy)

  const handleCheckedChange = async (checked: boolean) => {
    const from = comment.task
    setPending({ from, to: checked ? { doneBy: userId, doneAt: new Date().toISOString() } : {} })
    const changed = await (checked ? tick(comment) : untick(comment))
    if (!changed) {
      setPending(null)
      showToast(taskFailedToast(() => void handleCheckedChange(checked)))
    }
  }

  return (
    <div className={styles.row}>
      <Checkbox
        size="small"
        className={styles.checkbox}
        checked={isDone}
        aria-label={taskText(comment)}
        onCheckedChange={checked => void handleCheckedChange(checked)}
      />
      <div className={styles.text}>
        <div className={cn(isDone && styles.struck)} data-testid="task-text" data-done={String(isDone)}>
          {children}
        </div>
        {isDone ? <DoneLine task={task} /> : null}
      </div>
    </div>
  )
}
